/* ═══════════════════════════════════════════════════════════════
   AttentionStrip — "Needs attention" summary bar atop the Project Hub.
   Surfaces overdue/due-soon deliverables and unstaffed active
   projects (see projectMutations.computeAttention) so a PM sees
   what needs action before scrolling to the full table.

   Form: summary bar with disclosure (DESIGN.md §3.2). Collapsed by
   default for everyone — one slim row of count chips + a Show/Hide
   toggle. Expanding (or clicking a chip) reveals a dense, capped,
   internally-scrolling grid of individual items. Expanded state and
   the active kind filter persist per browser in localStorage so a PM
   who always expands it doesn't re-click every visit.
   ═══════════════════════════════════════════════════════════════ */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Clock3, UserX, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import type { AttentionItem, AttentionKind } from './projectMutations';
import type { DashboardFocus } from './widgets/context';

const KIND_ICON: Record<AttentionKind, LucideIcon> = {
  overdue: AlertTriangle,
  'due-soon': Clock3,
  'no-pm': UserX,
  'no-team': Users,
};
const KIND_COLOR: Record<AttentionKind, string> = {
  overdue: 'var(--dg)',
  'due-soon': 'var(--color-warning)',
  'no-pm': 'var(--tm)',
  'no-team': 'var(--tm)',
};
const KIND_LABEL: Record<AttentionKind, string> = {
  overdue: 'overdue',
  'due-soon': 'due soon',
  'no-pm': 'no PM',
  'no-team': 'no team',
};
const KIND_ORDER: AttentionKind[] = ['overdue', 'due-soon', 'no-pm', 'no-team'];

const STORAGE_KEY = 'sf.projects.attention.v1';

interface Persisted {
  expanded: boolean;
  activeKinds: AttentionKind[];
}

function loadPersisted(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { expanded: false, activeKinds: [] };
    const parsed = JSON.parse(raw);
    return {
      expanded: !!parsed.expanded,
      activeKinds: Array.isArray(parsed.activeKinds) ? parsed.activeKinds : [],
    };
  } catch {
    return { expanded: false, activeKinds: [] };
  }
}

export interface AttentionStripProps {
  items: AttentionItem[];
  status: 'loading' | 'ready' | 'error';
  onOpen: (projectId: string, focus?: DashboardFocus) => void;
}

function focusFor(item: AttentionItem): DashboardFocus | undefined {
  if (item.kind === 'overdue' || item.kind === 'due-soon') {
    return { kind: 'widget', widgetId: 'deliverables' };
  }
  return undefined;
}

export function AttentionStrip({ items, status, onOpen }: AttentionStripProps) {
  const initial = loadPersisted();
  const [expanded, setExpanded] = useState(initial.expanded);
  const [activeKinds, setActiveKinds] = useState<Set<AttentionKind>>(new Set(initial.activeKinds));

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ expanded, activeKinds: Array.from(activeKinds) })
      );
    } catch {
      /* ignore */
    }
  }, [expanded, activeKinds]);

  if (status === 'loading') {
    return (
      <div
        style={{
          height: rem(40),
          borderRadius: 'var(--radius-md)',
          background: 'var(--sa)',
          marginBottom: 'var(--space-3)',
          flexShrink: 0,
        }}
      />
    );
  }

  if (items.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: rem(28),
          marginBottom: 'var(--space-3)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--tm)',
          flexShrink: 0,
        }}
      >
        Nothing needs attention
      </div>
    );
  }

  const counts = new Map<AttentionKind, number>();
  for (const it of items) counts.set(it.kind, (counts.get(it.kind) || 0) + 1);

  function toggleKind(k: AttentionKind) {
    setActiveKinds((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
    setExpanded(true);
  }

  const shown = activeKinds.size === 0 ? items : items.filter((it) => activeKinds.has(it.kind));

  return (
    <div style={{ marginBottom: 'var(--space-3)', flexShrink: 0 }}>
      {/* Collapsed/summary row — always visible. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          height: rem(40),
          padding: `0 ${rem(2)}`,
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            color: 'var(--tm)',
            letterSpacing: '.02em',
            flexShrink: 0,
          }}
        >
          Needs attention
        </span>
        <div style={{ display: 'flex', gap: rem(6), flexWrap: 'wrap', flex: 1 }}>
          {KIND_ORDER.map((k) => {
            const n = counts.get(k) || 0;
            if (n === 0) return null;
            const active = activeKinds.has(k);
            return (
              <button
                key={k}
                type="button"
                onClick={() => toggleKind(k)}
                aria-pressed={active}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rem(4),
                  padding: `${rem(3)} ${rem(9)}`,
                  borderRadius: rem(999),
                  border: `1px solid ${active ? KIND_COLOR[k] : 'var(--bd)'}`,
                  background: active ? 'var(--sa)' : 'var(--sf)',
                  color: KIND_COLOR[k],
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 700,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                <Icon icon={KIND_ICON[k]} em={0.95} />
                {n} {KIND_LABEL[k]}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: rem(4),
            background: 'none',
            border: 'none',
            color: 'var(--ac)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: 'pointer',
            padding: `${rem(4)} ${rem(6)}`,
            flexShrink: 0,
          }}
        >
          {expanded ? 'Hide' : 'Show'}
          <Icon icon={expanded ? ChevronUp : ChevronDown} em={1} />
        </button>
      </div>

      {/* Expanded — dense, capped, internally-scrolling grid. */}
      {expanded && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(22rem, 1fr))',
            gap: rem(4),
            maxHeight: '30vh',
            overflowY: 'auto',
            padding: rem(4),
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--bd)',
            background: 'var(--sf)',
          }}
        >
          {shown.map((item) => {
            const KindIcon = KIND_ICON[item.kind];
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onOpen(item.projectId, focusFor(item))}
                title={item.detail}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rem(6),
                  height: rem(32),
                  padding: `0 ${rem(8)}`,
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  minWidth: 0,
                }}
              >
                <Icon
                  icon={KindIcon}
                  em={0.95}
                  style={{ color: KIND_COLOR[item.kind], flexShrink: 0 }}
                  aria-hidden="true"
                />
                <span style={{ fontWeight: 600, color: 'var(--tx)', flexShrink: 0 }}>
                  {item.title}
                </span>
                <span
                  style={{
                    color: 'var(--tm)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {item.detail}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
